// iter-386u-aq — Feature-Locked tooltip wrapper.
// Wraps any button/element that is gated by a plan capability. When the user's
// plan does NOT have the capability, the child is dimmed, clicks are
// intercepted, and hover shows "Upgrade to {plan} to unlock" with a direct
// button to /billing. When the capability IS available, the child renders
// completely unchanged.
import React from 'react';
import { Link } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { usePlanPermissions } from '@/hooks/usePlanPermissions';

const CAPABILITY_TO_MIN_PLAN = {
  can_export_pdf: 'Essential',
  can_export_excel: 'Essential',
  can_use_ai_task_creator: 'Essential',
  can_use_ai_tnc: 'Essential',
  can_use_ai_project_assistant: 'Pro-Intelligence',
  can_use_ai_boq_audit: 'Pro-Intelligence',
  can_view_ledger: 'Essential',
  can_publish_to_client: 'Essential',
  can_publish_to_vendor: 'Essential',
  can_use_marketplace: 'Essential',
  can_use_templates: 'Essential',
  can_charge_own_vendors: 'Enterprise',
  can_upload_logo: 'Enterprise',
  can_customize_theme: 'Enterprise',
};

export function FeatureLocked({ capability, label, children, className = '' }) {
  const { can, loaded } = usePlanPermissions();
  if (!loaded) return <>{children}</>;              // don't flicker during load
  if (can(capability)) return <>{children}</>;      // full access

  const minPlan = CAPABILITY_TO_MIN_PLAN[capability] || 'Pro-Intelligence';
  return (
    <div
      className={`relative inline-block group ${className}`}
      data-testid={`feature-locked-${capability}`}
    >
      <div
        className="opacity-50 cursor-not-allowed pointer-events-none select-none"
        aria-disabled="true"
      >{children}</div>
      <span
        className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-stone-900 text-white flex items-center justify-center text-[10px] shadow-md"
        aria-label="Locked"
      >
        <Lock size={11} />
      </span>
      <div
        className="pointer-events-none group-hover:pointer-events-auto absolute z-30 top-full mt-2 left-1/2 -translate-x-1/2 w-64 opacity-0 group-hover:opacity-100 transition-opacity bg-stone-900 text-white text-xs rounded-lg shadow-xl p-3"
        role="tooltip"
      >
        <div className="font-semibold mb-1">{label || 'This feature is locked'}</div>
        <div className="text-stone-300 leading-relaxed">
          Upgrade to <strong className="text-amber-300">{minPlan}</strong> to unlock this on every screen.
        </div>
        <Link
          to="/billing"
          className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-amber-300 hover:text-amber-200"
          data-testid={`feature-locked-upgrade-${capability}`}
        >
          View plans →
        </Link>
      </div>
    </div>
  );
}
